// Tubeframe-Joint field ROWS (Land → Tubeframe Joint).
//
// The individual input rows, factored out of LandContent so the SAME controls
// render in three places without duplication:
//   • LandContent — the legacy single pane (flag OFF, prod default).
//   • The "Dimensions" floating window (Units / O.D. / I.D.) — flag ON.
//   • The "Joint" floating window (Joint Angle / Mating Surface) — flag ON.
// All three read/write the shared tubeframeFields store, so the value is one
// source of truth regardless of which surface is showing.

import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHomepage } from '@/contexts/homepage-context';
import {
  useTubeframeFields,
  resetTubeframeFields,
} from './tubeframeFieldsStore';

// The "Dimensions" group — Units + tube O.D. + tube I.D.
export function TubeframeDimensionsFields({ enabled }: { enabled: boolean }) {
  const { units, od, id, setField } = useTubeframeFields();
  const { bumpDomainParams } = useHomepage();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-telekinesis-neutral-50">
          Units
        </span>
        <Select
          disabled={!enabled}
          value={units}
          onValueChange={(v) => {
            setField('units', v as 'in' | 'mm');
            bumpDomainParams();
          }}
        >
          <SelectTrigger className="h-7 w-28 rounded-md px-2 text-xs text-[#107d40]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
            <SelectItem value="in" className="text-xs">
              Inches
            </SelectItem>
            <SelectItem value="mm" className="text-xs">
              Millimeters
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-telekinesis-neutral-50">O.D.</span>
        <Input
          type="number"
          step="any"
          disabled={!enabled}
          value={od}
          onChange={(e) => {
            setField('od', e.target.value);
            bumpDomainParams();
          }}
          className="h-7 w-20 rounded-md px-2 text-center text-xs text-[#107d40]"
        />
        <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
          {units === 'in' ? '[in.]' : '[mm]'}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-telekinesis-neutral-50">I.D.</span>
        <Input
          type="number"
          step="any"
          disabled={!enabled}
          value={id}
          onChange={(e) => {
            setField('id', e.target.value);
            bumpDomainParams();
          }}
          className="h-7 w-20 rounded-md px-2 text-center text-xs text-[#107d40]"
        />
        <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
          {units === 'in' ? '[in.]' : '[mm]'}
        </span>
      </div>
    </div>
  );
}

// The "Joint" group — joint angle + mating surface.
export function TubeframeJointFields({ enabled }: { enabled: boolean }) {
  const { jointAngle, matingSurface, setField } = useTubeframeFields();
  const { bumpDomainParams } = useHomepage();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-telekinesis-neutral-50">
          Joint Angle
        </span>
        <Input
          type="number"
          step="any"
          disabled={!enabled}
          value={jointAngle}
          onChange={(e) => {
            setField('jointAngle', e.target.value);
            bumpDomainParams();
          }}
          className="h-7 w-20 rounded-md px-2 text-center text-xs text-[#107d40]"
        />
        <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
          [°]
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="flex-1 text-xs text-telekinesis-neutral-50">
          Mating Surface
        </span>
        <Select
          disabled={!enabled}
          value={matingSurface}
          onValueChange={(v) => {
            setField('matingSurface', v);
            bumpDomainParams();
          }}
        >
          <SelectTrigger className="h-7 w-28 rounded-md px-2 text-xs text-[#107d40]">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
            <SelectItem value="straight-wall" className="text-xs">
              Straight Wall
            </SelectItem>
            <SelectItem value="lattice" className="text-xs">
              Lattice
            </SelectItem>
            <SelectItem value="threaded" className="text-xs">
              Threaded
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Always-mounted syncer: publishes the shared field values up to
// homepage-context (tubeframeParamsReady + tubeframeParams) so the Generate
// button stays wired no matter which surface (single pane or split windows) is
// visible — the windows can unmount without dropping generation readiness.
export function TubeframeParamsSync() {
  const { units, od, id, jointAngle, matingSurface, ready } =
    useTubeframeFields();
  const { setTubeframeParamsReady, setTubeframeParams } = useHomepage();

  useEffect(() => {
    setTubeframeParamsReady(ready);
    setTubeframeParams(
      ready
        ? {
            od: od.trim(),
            id: id.trim(),
            angle: jointAngle.trim(),
            matingSurface,
            units,
          }
        : null,
    );
  }, [
    ready,
    od,
    id,
    jointAngle,
    matingSurface,
    units,
    setTubeframeParamsReady,
    setTubeframeParams,
  ]);

  // Clear the module-level store when the Land content unmounts, so field values
  // don't bleed across navigation/conversations (restores the pre-store
  // reset-on-unmount behaviour).
  useEffect(() => () => resetTubeframeFields(), []);

  return null;
}
